/**
 * Screen-local fetch module for the partner-config dry runs (epic 085 D9,
 * task 04): the campaign form and the endpoint editor send the partner slice
 * they would save and get the server's issues back. Follows the screen-local
 * `request()` pattern of `partner-endpoints/api.ts` — `lib/api.ts` is never
 * edited by a screen (ADR-010 A2 b). Wire types and the response schema come
 * from `@leads-platform/shared`; no local type mirror.
 */

import {
  apiResponseSchema,
  partnerConfigCheckResponseSchema,
  type CampaignConfigCheckRequestInput,
  type EndpointConfigCheckRequestInput,
  type PartnerConfigIssue,
} from '@leads-platform/shared';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const CHECK_PREFIX = '/admin/partner-config-check';

const checkResponseSchema = apiResponseSchema(partnerConfigCheckResponseSchema);

/**
 * POST one dry run and unwrap `{ data: { issues } }`. A non-2xx answer or a
 * body that is not the contract's shape rejects — the caller's hook turns any
 * rejection into "show nothing" (the save's own 400 stays the authority).
 */
async function postCheck(path: string, body: unknown): Promise<PartnerConfigIssue[]> {
  // HF-147: same options as lib/api.ts apiFetch — the admin session guard
  // 403s a POST without the X-Requested-With marker even with a live session
  // (session-guard CSRF rule), and credentials carries the cookie on
  // split-host stands.
  const response = await fetch(`${API_BASE_URL}${CHECK_PREFIX}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Partner-config check failed: ${response.status} ${response.statusText}`);
  }
  const json: unknown = await response.json();
  return checkResponseSchema.parse(json).data.issues;
}

/** POST /admin/partner-config-check/campaign — the campaign form's partner slice. */
export function checkCampaignPartnerConfig(
  body: CampaignConfigCheckRequestInput
): Promise<PartnerConfigIssue[]> {
  return postCheck('/campaign', body);
}

/** POST /admin/partner-config-check/endpoint — the endpoint editor's transport + variables. */
export function checkEndpointPartnerConfig(
  body: EndpointConfigCheckRequestInput
): Promise<PartnerConfigIssue[]> {
  return postCheck('/endpoint', body);
}
